import React, { useRef, useState } from "react";
import UnsavedChangesModal from "Components/Common/UnsavedChangesModal";
import Typography from "Components/DesignSystem/Typography/Typography";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import { Check, ChevronDown, ChevronRight } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING, FOCUS_RING_INSET } from "Components/DesignSystem/focusRing";
import { useDismissable } from "Components/DesignSystem/useDismissable";

type HierachicalDropdownProps = {
  id: string;
  columnOptions: any[];
  onClick: ((item: any) => void)[];
  selected: any[];
  data: any[][];
  isLoading?: { [key: number]: boolean };
  editInProgress:boolean;
  editSaveHandler?: (() => Promise<boolean>) | null;
  editCancelHandler?: (() => void) | null;
}

/**
 * The GMS workspace breadcrumb — Workspace › Domain › Channel › Environment — and
 * the four-column picker behind it.
 *
 * Restyled onto the design system 2026-08-30 (`okf/common/e2e.md` § 13). Every prop,
 * handler and state transition is unchanged: same `onClick[index](item)` contract,
 * same close-on-last-column, same click-an-already-selected-item-just-closes, same
 * loading gates, same unsaved-changes flow. What changed is what it looks like, plus
 * three defects the restyle had to resolve to be able to style anything at all.
 *
 * **1. Both label rows failed WCAG AA.** The breadcrumb's four column captions were
 * `#97A0AF` — **2.64:1** on white — and the picker's column headings were
 * `tw-text-gray-dark`, which resolves to Tailwind's stock `#9ca3af`, **2.54:1**. AA
 * body text needs 4.5:1. Both are now `ink-tertiary` (#6c778b, **4.52:1**), the role
 * the palette darkened from #6d788c specifically to clear that bar.
 *
 * **2. Caption and value were the same type.** Both were 12px/600; only colour told
 * them apart, and that colour was the failing one. So the strip had no hierarchy —
 * it read as eight items of equal weight. The caption is now `caption-metadata`
 * (12/400) and the value `title-header` (14/600): three axes of separation instead of
 * one. The trigger measures **52px either way** — the caption gives back the 2px the
 * value takes — so nothing in the 60px bar moves. The value's 14px also matches the
 * picker rows, where the same string used to be 12px in one place and 14px in the
 * other.
 *
 * **3. Selected and hovered were pixel-identical.** A chosen row was `#f4f5f7` and
 * the hover ground was *also* `#f4f5f7`, so hovering a selected row produced no state
 * change at all. Selection is now the one design-system grammar (§ 51/§ 65):
 * `accent` on `accent-tint`, the same pair a `Dropdown` option wears when chosen.
 *
 * **The panel no longer measures itself in JavaScript.** `calculateMaxHeight()`
 * modelled the layout in constants — `headerHeight = 32`, `itemHeight = 30`,
 * `emptyStateHeight = 60` — and wrote the result out as an inline pixel height. The
 * header measures **27px**, so the model was already 5px out of step with the CSS it
 * was predicting, and it applied the empty-state floor to *every* column: a
 * one-workspace column rendered 153px of white inside a 218px box, 27px of it dead
 * space inside a 92px `min-height` wrapped around 65px of content. It is now plain
 * CSS — `max-h` on each scroller, `items-stretch` on the row — so the panel takes its
 * height from real content and can never drift from the type scale again.
 */
const HierachicalDropdown = ({ id, columnOptions, onClick, selected, data, isLoading = {}, editInProgress, editSaveHandler, editCancelHandler }: HierachicalDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingClick, setPendingClick] = useState<{ index: number; item: any } | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);

  const isAnyLoading = Object.values(isLoading).some(loading => loading === true);
  const hasHandlers = editSaveHandler && editCancelHandler;

  /*
    Was a hand-rolled `mousedown` listener via `Utils.run_callback_on_click_outside_ref`.
    Same event, same containment test, same close — plus Escape, which this control
    never had. Non-modal: no scroll lock, no focus trap, nothing else moves.
  */
  useDismissable({
    isOpen,
    onDismiss: () => setIsOpen(false),
    isInside: (target) => !!ref.current?.contains(target),
  });

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, index: number, item: any) => {
    e.preventDefault();

    if (isAnyLoading) {
      console.log('Operation in progress, please wait...');
      return;
    }

    if (isLoading[index]) {
      console.log(`Column ${index} is loading, please wait...`);
      return;
    }

    // Close dropdown if clicking already-selected item
    const option = columnOptions[index];
    const isAlreadySelected = selected?.[index]?.[option.idColumn] === item?.[option.idColumn];
    if (isAlreadySelected) {
      setIsOpen(false);
      return;
    }

    onClick[index](item);
    if (index + 1 === data.length) setIsOpen(false);
  };

  const handleToggleClick = () => {
    if (isAnyLoading) return;

    if (editInProgress && hasHandlers) {
      setShowUnsavedModal(true);
      return;
    }

    setIsOpen(!isOpen);
  };

  const executePendingClick = (clickData: { index: number; item: any } | null) => {
    if (clickData) {
      onClick[clickData.index](clickData.item);
    }
  };

  const handleModalSave = async () => {
    if (!editSaveHandler) return;
    setSaveInProgress(true);
    try {
      const success = await editSaveHandler();
      if (success) {
        setShowUnsavedModal(false);
        executePendingClick(pendingClick);
        setPendingClick(null);
        setIsOpen(false);
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleModalDiscard = () => {
    editCancelHandler?.();
    setShowUnsavedModal(false);
    executePendingClick(pendingClick);
    setPendingClick(null);
    setIsOpen(false);
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setPendingClick(null);
  };

  return (
    <>
      <div id={id} className={`tw-relative`} ref={ref}>
        <button data-testid="common-hierachicaldropdown-button-1"
          id={`${id}-toggle-button`}
          type="button"
          onClick={handleToggleClick}
          disabled={isAnyLoading || (editInProgress && !hasHandlers)}
          aria-haspopup="true"
          aria-expanded={isOpen}
          className={`tw-h-full tw-flex tw-items-center tw-rounded-ctl tw-border tw-border-edge tw-py-1.5 tw-px-2 tw-w-auto tw-transition-colors ${FOCUS_RING} ${
            isOpen ? 'tw-bg-surface-sunken' : 'hover:tw-bg-surface-hover-strong'
          } ${
            isAnyLoading ? 'tw-opacity-60 tw-cursor-wait' : 'tw-cursor-pointer'
          }`}
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            {columnOptions?.map((option: any, index: number) => (
              <React.Fragment key={index}>
                <div className="tw-flex tw-flex-col tw-items-start">
                  <Typography variant="sub-title-label" as="span" className="tw-text-ink-tertiary tw-mb-0.5">
                    {option.title}
                  </Typography>
                  {/*
                    `h-5` is the `title-header` line-height, held explicitly so the
                    skeleton below occupies exactly the space its value will and the
                    breadcrumb does not jog when a column resolves.
                  */}
                  <span
                    id={`${option.title}-item-${index}`}
                    className="tw-flex tw-h-5 tw-items-center tw-text-title-header tw-text-ink"
                  >
                    {isLoading[index] ? (
                      /* Was the word `Loading…` under a hand-rolled pulse — the blink
                         the Skeleton primitive rejected (§ 63), and the last entry this
                         file had on that ratchet. A bar where the value will be says
                         "this is arriving"; the word "Loading" in the slot a workspace
                         name occupies reads as a workspace called Loading. */
                      <Skeleton variant="text" width="80px" data-testid={`${id}-value-loading-${index}`} />
                    ) : (
                      selected[index] ? selected[index][option.nameColumn] : '-'
                    )}
                  </span>
                </div>
                {index < columnOptions.length - 1 && (
                  <ChevronRight size={16} className="tw-shrink-0 tw-text-edge" aria-hidden="true" />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className={`tw-ml-3 tw-flex tw-text-ink-tertiary tw-transition-transform tw-duration-200 ${isOpen ? 'tw-rotate-180' : ''}`}>
            <ChevronDown size={16} aria-hidden="true" />
          </div>
        </button>

        {isOpen && (
          <div id={`${id}-container`} className={`tw-absolute tw-top-full tw-left-0 tw-shadow-ds-md tw-z-50 tw-mt-1`}>
            {/*
              `items-stretch` (the flex default, stated because it is load-bearing) is
              what squares the columns off against the tallest one. Height comes from
              content — no inline pixel height, no JS model of the row box.
            */}
            <div
              id={`${id}-content`}
              className={`tw-relative tw-flex tw-items-stretch tw-bg-surface tw-rounded-srf tw-overflow-hidden tw-border tw-border-edge`}
            >
              {columnOptions?.map((option: any, index: number) => (
                <div
                  key={index}
                  className={`tw-flex tw-flex-col tw-min-w-[168px] tw-max-w-[240px] ${
                    index < columnOptions.length - 1 ? 'tw-border-r tw-border-edge' : ''
                  } ${isLoading[index] ? 'tw-opacity-50' : ''}`}
                >
                  {/*
                    OUTSIDE the scroller. It used to be the first `<li>` of the list,
                    so scrolling an eight-channel column scrolled its own "Channel"
                    heading out of view — and a heading is not a list item anyway.
                  */}
                  <Typography
                    variant="sub-title-label"
                    as="div"
                    id={`${option.title}`}
                    /* The type token goes on the BLOCK, not on a span inside it. A
                       plain div inherits the body's 16px/normal strut, so its line box
                       stays 24px however small the span within it is — measured 37px
                       tall per heading before this, 29px after. */
                    className="tw-shrink-0 tw-border-b tw-border-edge tw-px-3 tw-py-1 tw-text-ink-tertiary"
                  >
                    {option.title}
                  </Typography>

                  {/* 152px = six 24px rows + the list's own 8px of padding; measured
                      at exactly 6.00 visible rows. A CAP, not a prediction — get it
                      slightly wrong and a seventh row is sliced, which merely reads as
                      a scroll affordance. The constants this replaced predicted the
                      whole box and were wrong by 5px, which cost real layout. */}
                  <ul
                    id={`${option.title}-container`}
                    className={`tw-flex-1 tw-overflow-y-auto tw-py-1 tw-max-h-[152px]`}
                  >
                    {isLoading[index] ? (
                      <li className="tw-flex tw-justify-center tw-px-3 tw-py-4">
                        <Spinner size="sm" label="Loading..." showLabel data-testid={`${id}-column-loading-${index}`} />
                      </li>
                    ) : (
                      <>
                        {(!data[index] || !Array.isArray(data[index]) || data[index].length <= 0) && (
                          <li id={`${option.title}-no-data`}>
                            <Typography variant="caption-metadata" as="div" className="tw-px-3 tw-py-2 tw-text-left tw-w-full tw-text-ink-tertiary">
                              No {option.title.toLowerCase()}s available
                            </Typography>
                          </li>
                        )}
                        {data[index] && Array.isArray(data[index]) &&
                          data[index].map((item: any, key: number) => {
                            const isSelected = selected?.[index]?.[option.idColumn] === item?.[option.idColumn];
                            /* Keyed on the item's own id, not the row index: four
                               columns of `…-button-2-0` meant one testid matched four
                               elements, and the tests had to reach for
                               `getAllByTestId(...)[n]` to work around it. */
                            const optionId = `${id}-option-${index}-${item?.[option.idColumn]}`;
                            return (
                              <li id={`${option.title}-${key}`} key={optionId}>
                                <button data-testid={optionId}
                                  id={optionId}
                                  type="button"
                                  onClick={(e) => handleClick(e, index, item)}
                                  disabled={isAnyLoading}
                                  aria-current={isSelected ? 'true' : undefined}
                                  className={`tw-px-3 tw-py-1 tw-text-left tw-w-full tw-text-sub-body-supporting tw-flex tw-items-center tw-justify-between tw-gap-2 tw-transition-colors ${FOCUS_RING_INSET} ${
                                    isSelected
                                      ? 'tw-bg-accent-tint tw-text-accent'
                                      : 'tw-text-ink hover:tw-bg-surface-hover-strong'
                                  } ${isAnyLoading ? 'tw-cursor-wait' : 'tw-cursor-pointer'}`}
                                  title={item[option.nameColumn]}
                                >
                                  <span className="tw-truncate">{item[option.nameColumn]}</span>
                                  {isSelected && (
                                    <Check size={14} className="tw-shrink-0" aria-hidden="true" />
                                  )}
                                </button>
                              </li>
                            );
                          })}
                      </>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {showUnsavedModal && (
        <UnsavedChangesModal
          id="workspace-unsaved-changes-modal"
          title="Unsaved Changes"
          message="You have unsaved changes. Do you want to save them before switching?"
          confirmText="Save Changes"
          cancelText="Cancel"
          discardText="Discard changes"
          onClose={handleModalCancel}
          onConfirm={handleModalSave}
          onDiscard={handleModalDiscard}
          saveInProgress={saveInProgress}
        />
      )}
    </>
  );
};

export default HierachicalDropdown;
